import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Zap, CheckCircle2, RotateCcw, ArrowRight, Flame, Target } from 'lucide-react';

const CHALLENGES = [
  {
    id: 'vendor-sprawl',
    title: 'Vendor Sprawl',
    scenario: 'Your company operates in 25 countries with 8 different compliance vendors. Each vendor has a separate contract, integration, and support team.',
    question: 'What is the biggest hidden cost of managing 8 compliance vendors?',
    options: [
      { text: 'Software licensing fees', correct: false },
      { text: 'Internal coordination & integration overhead', correct: true },
      { text: 'Training costs for different UIs', correct: false },
      { text: 'Data storage duplication', correct: false },
    ],
    explanation: 'Internal coordination typically costs 2-3x more than the vendor fees themselves. Each integration point needs maintenance, each vendor needs management, and troubleshooting spans multiple platforms.',
    points: 100,
  },
  {
    id: 'migration-timing',
    title: 'Migration Timing',
    scenario: 'Your S/4HANA go-live is in 9 months. The tax compliance workstream hasn\'t been scoped yet. The project manager says "we\'ll handle compliance after go-live."',
    question: 'What typically happens when compliance is deferred to post-go-live?',
    options: [
      { text: 'Everything works fine — compliance can always be added later', correct: false },
      { text: 'Minor delays of 1-2 weeks', correct: false },
      { text: 'Go-live extension of 3-6 months and 1.5-2x higher implementation cost', correct: true },
      { text: 'Compliance automatically transfers from ECC to S/4', correct: false },
    ],
    explanation: 'Retrofitting compliance after go-live is consistently 1.5-2x more expensive than addressing it during migration. Late discovery of gaps has extended go-live timelines by 3-6 months in documented cases.',
    points: 150,
  },
  {
    id: 'data-model',
    title: 'Data Model Shock',
    scenario: 'During S/4HANA migration, your team discovers that the Universal Journal (ACDOCA) restructures how tax-relevant data flows through the system. Your legacy compliance tool was built for ECC\'s data model.',
    question: 'What is the primary risk with legacy compliance tools during S/4 data model changes?',
    options: [
      { text: 'The UI looks different', correct: false },
      { text: 'Report formats change slightly', correct: false },
      { text: 'Tax determination rules, reporting configs, and data mappings break silently', correct: true },
      { text: 'Performance degrades marginally', correct: false },
    ],
    explanation: 'The shift to ACDOCA and simplified data models means chart of accounts restructuring, new material types, and business partner changes all impact tax data flow. Legacy tools designed for ECC break — often silently, creating compliance gaps discovered during audits.',
    points: 200,
  },
  {
    id: 'mandate-wave',
    title: 'The Mandate Wave',
    scenario: 'France, Poland, and Belgium have all announced new mandatory e-Invoicing requirements. Your S/4HANA migration project is at peak intensity. Your compliance vendor says they "plan to support" these mandates by next year.',
    question: 'What is the optimal approach to handling new mandates during an active migration?',
    options: [
      { text: 'Wait for the current vendor to add support', correct: false },
      { text: 'Build custom ABAP solutions for each mandate', correct: false },
      { text: 'Deploy a platform that absorbs new mandates through automatic updates', correct: true },
      { text: 'Defer compliance for those countries until after migration', correct: false },
    ],
    explanation: 'A modern platform-based approach means new mandates are absorbed through platform updates — not separate projects. This is the only approach that scales when multiple countries introduce requirements simultaneously.',
    points: 150,
  },
  {
    id: 'tco-blindspot',
    title: 'TCO Blindspot',
    scenario: 'Your CFO asks: "What is our total annual spend on tax compliance across all countries?" You check with finance, IT, and the tax team — nobody has a single answer.',
    question: 'Why is total compliance cost so hard to track for most multinationals?',
    options: [
      { text: 'Finance teams lack proper accounting software', correct: false },
      { text: 'Costs are spread across multiple budgets, vendors, and internal labor pools', correct: true },
      { text: 'Tax compliance costs are too small to track', correct: false },
      { text: 'ERP systems don\'t support cost tracking', correct: false },
    ],
    explanation: 'Compliance costs hide everywhere: vendor licenses across multiple budgets, internal FTE time, integration maintenance by IT, consulting fees, and penalty costs. Platform consolidation creates a single, visible cost line for the first time.',
    points: 100,
  },
];

const BADGES = [
  { id: 'first-answer', name: 'First Steps', desc: 'Complete your first challenge', icon: Star, threshold: 1, color: '#f59e0b' },
  { id: 'streak-3', name: 'On a Roll', desc: 'Get 3 correct answers in a row', icon: Flame, threshold: 3, color: '#ef4444' },
  { id: 'all-correct', name: 'Compliance Expert', desc: 'Get all answers correct', icon: Trophy, threshold: 5, color: '#8b5cf6' },
  { id: 'complete', name: 'Challenge Complete', desc: 'Finish all 5 challenges', icon: Target, threshold: 5, color: '#14b8a6' },
];

const SapGameChallenge = () => {
  const [currentChallenge, setCurrentChallenge] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [started, setStarted] = useState(false);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [showBadge, setShowBadge] = useState(null);

  const challenge = CHALLENGES[currentChallenge];

  const checkBadges = (newCorrectCount, newStreak, isComplete) => {
    const newBadges = [];
    if (newCorrectCount >= 1 && !earnedBadges.includes('first-answer')) newBadges.push('first-answer');
    if (newStreak >= 3 && !earnedBadges.includes('streak-3')) newBadges.push('streak-3');
    if (newCorrectCount >= 5 && !earnedBadges.includes('all-correct')) newBadges.push('all-correct');
    if (isComplete && !earnedBadges.includes('complete')) newBadges.push('complete');

    if (newBadges.length > 0) {
      setEarnedBadges(prev => [...prev, ...newBadges]);
      setShowBadge(newBadges[0]);
      setTimeout(() => setShowBadge(null), 2500);
    }
  };

  const handleAnswer = (idx) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(idx);
    const isCorrect = challenge.options[idx].correct;
    let newStreak = streak;
    let newCorrectCount = correctCount;

    if (isCorrect) {
      const bonus = streak >= 2 ? 50 : 0;
      setScore(s => s + challenge.points + bonus);
      newStreak = streak + 1;
      setStreak(newStreak);
      setMaxStreak(Math.max(maxStreak, newStreak));
      newCorrectCount = correctCount + 1;
      setCorrectCount(newCorrectCount);
    } else {
      newStreak = 0;
      setStreak(0);
    }

    setShowExplanation(true);
    checkBadges(newCorrectCount, newStreak, currentChallenge === CHALLENGES.length - 1);
  };

  const handleNext = () => {
    if (currentChallenge < CHALLENGES.length - 1) {
      setCurrentChallenge(c => c + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setCompleted(true);
    }
  };

  const handleRestart = () => {
    setCurrentChallenge(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setStreak(0);
    setMaxStreak(0);
    setCorrectCount(0);
    setCompleted(false);
    setEarnedBadges([]);
  };

  if (!started) {
    return (
      <div className="text-center py-8" data-testid="sap-game-start">
        <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <Trophy className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-2xl font-bold text-taxera-charcoal mb-3">
          The Compliance Challenge
        </h3>
        <p className="text-sm text-slate-500 mb-2 max-w-md mx-auto">
          Test your knowledge of SAP S/4HANA migration compliance risks. 5 real-world scenarios. Can you get a perfect score?
        </p>
        <div className="flex items-center justify-center gap-4 text-xs text-slate-400 mb-6">
          <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> 5 challenges</span>
          <span className="flex items-center gap-1"><Star className="w-3 h-3" /> Earn badges</span>
          <span className="flex items-center gap-1"><Trophy className="w-3 h-3" /> Score points</span>
        </div>
        <button
          onClick={() => setStarted(true)}
          className="group inline-flex items-center gap-2 px-8 py-4 bg-amber-500 text-white rounded-full font-semibold hover:bg-amber-600 hover:shadow-lg transition-all hover:scale-105"
          data-testid="sap-game-start-btn"
        >
          Start the Challenge
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    );
  }

  if (completed) {
    const pct = Math.round((correctCount / CHALLENGES.length) * 100);
    const grade = pct === 100 ? 'Flawless!' : pct >= 80 ? 'Excellent!' : pct >= 60 ? 'Well done!' : 'Keep learning!';
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6" data-testid="sap-game-results">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mx-auto mb-4">
          <Trophy className="w-10 h-10 text-white" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-2xl font-bold text-taxera-charcoal mb-1">{grade}</h3>
        <div className="font-heading text-5xl font-bold text-amber-500 mb-2">{score}</div>
        <p className="text-sm text-slate-500 mb-6">points earned</p>

        <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto mb-6">
          <div className="bg-slate-50 rounded-xl p-3">
            <div className="text-lg font-bold text-taxera-charcoal">{correctCount}/{CHALLENGES.length}</div>
            <div className="text-2xs text-slate-400">Correct</div>
          </div>
          <div className="bg-slate-50 rounded-xl p-3">
            <div className="text-lg font-bold text-taxera-charcoal">{maxStreak}</div>
            <div className="text-2xs text-slate-400">Best streak</div>
          </div>
          <div className="bg-slate-50 rounded-xl p-3">
            <div className="text-lg font-bold text-taxera-charcoal">{earnedBadges.length}</div>
            <div className="text-2xs text-slate-400">Badges</div>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          {BADGES.map((badge) => {
            const Icon = badge.icon;
            const earned = earnedBadges.includes(badge.id);
            return (
              <div key={badge.id} className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium transition-all ${earned ? 'bg-white border border-slate-200 shadow-sm' : 'bg-slate-100 text-slate-400'}`}>
                <Icon className="w-3.5 h-3.5" style={earned ? { color: badge.color } : {}} strokeWidth={1.5} />
                <span className={earned ? 'text-slate-700' : ''}>{badge.name}</span>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          <button onClick={handleRestart} className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-full text-sm font-semibold hover:bg-slate-50 transition-colors" data-testid="sap-game-restart">
            <RotateCcw className="w-4 h-4" /> Try Again
          </button>
          <a href="/contact" className="group inline-flex items-center gap-2 px-6 py-3 taxera-gradient text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all" data-testid="sap-game-cta">
            Talk to an Expert <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </motion.div>
    );
  }

  return (
    <div data-testid="sap-game-active">
      {/* Badge popup */}
      <AnimatePresence>
        {showBadge && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            className="fixed top-24 left-1/2 -translate-x-1/2 z-50 bg-white rounded-2xl border border-slate-200 shadow-xl px-6 py-4 flex items-center gap-3"
          >
            {(() => {
              const badge = BADGES.find(b => b.id === showBadge);
              const Icon = badge.icon;
              return (
                <>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${badge.color}20` }}>
                    <Icon className="w-5 h-5" style={{ color: badge.color }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-amber-500 uppercase tracking-wide">Badge Earned!</div>
                    <div className="text-sm font-bold text-taxera-charcoal">{badge.name}</div>
                  </div>
                </>
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Score bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-sm">
            <Trophy className="w-4 h-4 text-amber-500" />
            <span className="font-bold text-taxera-charcoal">{score}</span>
            <span className="text-slate-400">pts</span>
          </div>
          {streak >= 2 && (
            <div className="flex items-center gap-1 px-2 py-0.5 bg-red-50 rounded-full">
              <Flame className="w-3 h-3 text-red-500" />
              <span className="text-xs font-bold text-red-500">{streak}x streak!</span>
            </div>
          )}
        </div>
        <span className="text-xs text-slate-400">{currentChallenge + 1} / {CHALLENGES.length}</span>
      </div>

      {/* Progress */}
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-6">
        <motion.div className="h-full bg-amber-500 rounded-full" animate={{ width: `${((currentChallenge) / CHALLENGES.length) * 100}%` }} />
      </div>

      {/* Challenge */}
      <motion.div key={currentChallenge} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-xs font-semibold text-slate-500 mb-4">
          <Target className="w-3 h-3" /> {challenge.title}
        </div>

        <div className="bg-slate-50 rounded-xl p-4 mb-5 border border-slate-100">
          <p className="text-sm text-slate-600 leading-relaxed italic">{challenge.scenario}</p>
        </div>

        <h4 className="text-base font-bold text-taxera-charcoal mb-4">{challenge.question}</h4>

        <div className="space-y-2.5">
          {challenge.options.map((opt, idx) => {
            const isSelected = selectedAnswer === idx;
            const isCorrect = opt.correct;
            let borderColor = 'border-slate-200';
            let bgColor = 'bg-white';
            if (selectedAnswer !== null) {
              if (isCorrect) { borderColor = 'border-emerald-400'; bgColor = 'bg-emerald-50'; }
              else if (isSelected && !isCorrect) { borderColor = 'border-red-400'; bgColor = 'bg-red-50'; }
            }
            return (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                disabled={selectedAnswer !== null}
                className={`w-full text-left p-4 rounded-xl border transition-all ${borderColor} ${bgColor} ${
                  selectedAnswer === null ? 'hover:border-slate-300 hover:shadow-md cursor-pointer' : 'cursor-default'
                }`}
                data-testid={`sap-game-option-${idx}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    selectedAnswer !== null && isCorrect ? 'bg-emerald-500 text-white' :
                    selectedAnswer !== null && isSelected && !isCorrect ? 'bg-red-500 text-white' :
                    'bg-slate-100 text-slate-500'
                  }`}>
                    {selectedAnswer !== null && isCorrect ? <CheckCircle2 className="w-4 h-4" /> :
                     String.fromCharCode(65 + idx)}
                  </div>
                  <span className="text-sm text-slate-700">{opt.text}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Explanation */}
        <AnimatePresence>
          {showExplanation && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-5"
            >
              <div className={`rounded-xl p-5 border ${
                challenge.options[selectedAnswer]?.correct
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {challenge.options[selectedAnswer]?.correct ? (
                    <span className="text-xs font-bold text-emerald-600">Correct! +{challenge.points}{streak >= 2 ? ' +50 streak bonus' : ''} points</span>
                  ) : (
                    <span className="text-xs font-bold text-amber-600">Not quite — here's why:</span>
                  )}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{challenge.explanation}</p>
              </div>
              <button
                onClick={handleNext}
                className="mt-4 group inline-flex items-center gap-2 px-6 py-3 taxera-gradient text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all"
                data-testid="sap-game-next"
              >
                {currentChallenge < CHALLENGES.length - 1 ? 'Next Challenge' : 'See Results'}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default SapGameChallenge;
